/**
 * Persisted "active tenant" selection — shared by TenantProvider
 * (src/layout/TenantSwitcher.tsx, which reads it on mount and writes it as
 * the tenant resolves) and AuthContext (src/auth/AuthContext.tsx, which
 * clears it on logout).
 *
 * Lives in its own module, outside both src/layout/ and AuthContext.tsx, to
 * avoid a circular import: TenantSwitcher.tsx already imports useAuth from
 * AuthContext.tsx, so AuthContext.tsx importing FROM TenantSwitcher.tsx
 * would form a cycle. This module depends on neither.
 *
 * Bug fixed here (Finding 3, fix-wave 2026-07-24 — CRITICAL): the persisted
 * value used to be a bare tenant-id string under a single unscoped
 * `active_tenant` localStorage key, and AuthContext.logout() never cleared
 * it. On a shared browser, user B logging in right after user A logged out
 * silently inherited user A's tenant selection — for an identity provider
 * that is a strictly worse failure than the stuck-spinner bug it was fixed
 * alongside (291c525f / 19188058). The persisted value is now stored
 * TOGETHER WITH the subject it was resolved for, and is only ever read back
 * for that SAME subject; a persisted value with no subject, a malformed
 * (legacy) value, or one belonging to a different subject is untrusted and
 * ignored. logout() also clears the key outright as defense-in-depth (see
 * AuthContext.tsx).
 */

const ACTIVE_TENANT_KEY = 'active_tenant'

interface PersistedActiveTenant {
  sub: string
  tenant: string
}

function isPersistedActiveTenant(
  value: unknown
): value is PersistedActiveTenant {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Partial<PersistedActiveTenant>).sub === 'string' &&
    typeof (value as Partial<PersistedActiveTenant>).tenant === 'string'
  )
}

/**
 * Reads the persisted active tenant, but ONLY if it was persisted for `sub`.
 * Returns '' when there is nothing persisted, the stored value is
 * malformed/legacy (a bare string from before this fix, or corrupt JSON), or
 * — critically — it belongs to a different (or unknown) subject.
 * localStorage is unauthenticated, shared-origin storage: it must never be
 * trusted across identities.
 */
export function readPersistedActiveTenant(sub: string | null): string {
  if (!sub) return ''
  try {
    const raw = localStorage.getItem(ACTIVE_TENANT_KEY)
    if (!raw) return ''
    const parsed: unknown = JSON.parse(raw)
    if (isPersistedActiveTenant(parsed) && parsed.sub === sub) {
      return parsed.tenant
    }
    return ''
  } catch {
    return ''
  }
}

/**
 * Persists `tenant` as belonging to `sub` so it survives a refresh. No-op
 * when either is empty/unknown — an anonymous entry can never be validated
 * against a future reader, so it must not be written at all.
 */
export function writePersistedActiveTenant(
  sub: string | null,
  tenant: string
): void {
  if (!sub || !tenant) return
  try {
    const payload: PersistedActiveTenant = { sub, tenant }
    localStorage.setItem(ACTIVE_TENANT_KEY, JSON.stringify(payload))
  } catch {
    // Storage unavailable (private mode / quota) — degrade gracefully.
  }
}

/**
 * Clears the persisted active tenant outright. Called from
 * AuthContext.logout() so the NEXT user to authenticate in this browser
 * (e.g. a shared kiosk) can never inherit even a transient trace of the
 * previous user's tenant selection.
 */
export function clearPersistedActiveTenant(): void {
  try {
    localStorage.removeItem(ACTIVE_TENANT_KEY)
  } catch {
    // ignore
  }
}
